import { FC, Fragment } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'hooks/useToggle';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { deleteMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectCurrentUser } from 'store/currentUser/currentUserSelectors';
import { selectCkeckedMediaIds } from 'store/media/mediaSelectors';
import UserRoles from 'types/UserRoles';
import IMedia from 'models/Media';
// Components
import { IconButton } from 'components/Buttons';
import MediaPreviewDialog from 'dialogs/MediaPreview.dialog';
import ConfirmationDialog from 'dialogs/Confirmation.dialog';
import { Checkbox } from 'components/Controls';
import AudioPlayer from 'components/AudioPlayer';
// Icons
import { FaTrash, FaEye } from 'react-icons/fa';

type Props = {
  media: IMedia;
}

const MediaItem:FC<Props> = ({ media }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const currentUser = useAppSelector(selectCurrentUser);
  const ckeckedMediaIds = useAppSelector(selectCkeckedMediaIds);

  const isChecked = ckeckedMediaIds.includes(media._id);

  const handleCheckMedia = () => {
    if (isChecked) {
      dispatch(mediaActions.setCheckedMedia(ckeckedMediaIds.filter(mediaId => mediaId !== media._id)));
    } else {
      dispatch(mediaActions.setCheckedMedia([...ckeckedMediaIds, media._id]));
    }
  }

  const handleDelete = () => {
    dispatch(deleteMedia(media._id));
  }

  const { open:openPreview, toggle:togglePreview } = useToggle();
  const { open:openDeleteDialog, toggle:toggleDeleteDialog } = useToggle();

  const isAdmin = currentUser?.groups.includes(UserRoles.Admin);
  
  return (
    <Fragment>
      {openPreview ? (
        <MediaPreviewDialog open={openPreview} onClose={togglePreview} media={media} />
      ) : null}

      {openDeleteDialog ? (
        <ConfirmationDialog
          open={openDeleteDialog}
          onClose={toggleDeleteDialog}
          title={t('pages.media.deleteTitle')}
          content={t('pages.media.deleteConfirmation')}
          loading={false}
          onConfirm={handleDelete}
        />
      ) : null}

      <div className="w-48 h-48 relative border">
        {media.type === 'image' ? (
          <div className="w-full h-full cursor-pointer" style={{
            background: `url('${media.href}') center / contain no-repeat`,
          }} />
        ) : null}
        {media.type === 'video' ? (
          <video className="w-full h-full object-center object-cover" controls>
            <source src={media.href} type="video/mp4" />
          </video>
        ) : null}
        {media.type === 'audio' ? (
          <div className="w-full h-full flex flex-col justify-end bg-black/30">
            <p className="text-white text-sm break-all p-1">{media.title || ''}</p>
            <AudioPlayer src={media.href} />
          </div>
        ) : null}
        {isAdmin ? (
          <div className="p-2 pr-0 absolute top-0 left-0 bg-gray-800 rounded">
            <Checkbox
              label=""
              id={`media-checkbox-${media._id}`} name={`media-checkbox-${media._id}`}
              checked={isChecked}
              onChange={handleCheckMedia}
              className="m-0"
            />
          </div>
        ) : null}
        <div className="absolute top-0 right-0 bg-gray-800 rounded">
          <IconButton size="sm" onClick={togglePreview}>
            <FaEye />
          </IconButton>
          <IconButton size="sm" onClick={toggleDeleteDialog} className="hover:text-red-600">
            <FaTrash />
          </IconButton>
        </div>
      </div>
    </Fragment>
  )
}

export default MediaItem;
